import { z } from "zod";
import { type CampaignAction, campaignActionSchema } from "./campaign-config.ts";

/** Public vocabulary promoted from the reviewed gold-6 definition, never private corpus text. */
export const ReplyStanceSchema = z.enum([
  "interested_engaged",
  "brushoff_soft_yes",
  "curiosity_no_intent",
  "timing_defer",
  "not_interested_soft",
  "not_interested_hard",
  "opt_out_request",
  "hostile_complaint",
  "legal_privacy_demand",
  "auto_reply_ooo",
  "auto_reply_other",
  "role_change_departure_auto_notice",
  "unrelated_correspondence",
  "reverse_pitch_partnership",
  "not_a_lead_reply",
  "other",
]);
export const ReplyAskSchema = z.enum([
  "send_materials",
  "answer_pricing",
  "confirm_scope",
  "route_to_person",
  "propose_times",
  "answer_data_source",
  "remove_me",
  "stop_until_date",
  "add_attendees",
  "answer_technical",
  "confirm_meeting_details",
  "provide_references",
  "reschedule",
  "sign_nda",
  "route_to_procurement",
  "confirm_their_time",
  "register_attendee",
  "other",
]);
export const ReplyRouteSchema = z.enum([
  "none",
  "referral_named_with_address",
  "referral_named_no_address",
  "wrong_person_unnamed",
  "gatekeeper_response",
  "role_change_left_company",
  "role_change_internal_remit",
  "role_change_left_to_new_icp_company",
]);
export const ReplyContextFactSchema = z.enum(["has_incumbent", "meeting_already_booked"]);
export const AskAuthoritySchema = z.enum(["agency", "client", "either"]);
/** Default answer authority, never proof of permission to execute an action. */
export const ASK_AUTHORITY = {
  send_materials: "agency",
  answer_pricing: "client",
  confirm_scope: "client",
  route_to_person: "client",
  propose_times: "client",
  answer_data_source: "either",
  remove_me: "agency",
  stop_until_date: "agency",
  add_attendees: "agency",
  answer_technical: "client",
  confirm_meeting_details: "either",
  provide_references: "client",
  reschedule: "agency",
  sign_nda: "client",
  route_to_procurement: "client",
  confirm_their_time: "client",
  register_attendee: "agency",
  other: "client",
} as const satisfies Record<z.infer<typeof ReplyAskSchema>, z.infer<typeof AskAuthoritySchema>>;
/**
 * The authority table as the model sees it. Generated from the constant: the analyst is graded
 * against this table, so a hand-written paraphrase would let the prompt and the check drift apart.
 */
const ASK_AUTHORITY_GUIDE = `Every ask has a fixed authority: the side that must authorize its answer. ${Object.entries(
  ASK_AUTHORITY,
)
  .map(([ask, authority]) => `${ask}=${authority}`)
  .join(
    ", ",
  )}. A speech act bound to an ask whose authority is client must have owner client, whoever wrote the sentence and whether or not an approved answer already exists. When one sentence both accepts and requests, you may bind the ask to a separate act for the request.`;
const text = z.string().trim().min(1);
const unique = <T>(items: T[]) => new Set(items).size === items.length;
const ClassificationObjectSchema = z.strictObject({
  version: z.literal("gold-6-v2"),
  stance: ReplyStanceSchema.describe(
    "interested_engaged includes conditional interest and acceptance of a call, event seat or contribution. brushoff_soft_yes means yes ONLY to receiving materials; an explicit yes to attending an event is interested_engaged, even if the next request is its registration link.",
  ),
  asks: z.array(ReplyAskSchema).refine(unique, "Asks must be unique"),
  route: ReplyRouteSchema,
  context: z.array(ReplyContextFactSchema).refine(unique, "Context facts must be unique"),
  other_stance: text.nullable(),
  other_ask: text.nullable(),
  evidence_ids: z.array(text).min(1).refine(unique, "Evidence IDs must be unique"),
  ask_bindings: z
    .array(
      z.strictObject({
        ask: ReplyAskSchema,
        speech_act_ids: z.array(text).min(1).refine(unique, "Speech act IDs must be unique"),
        timing: z.enum(["now", "later"]),
      }),
    )
    .describe(ASK_AUTHORITY_GUIDE),
});
export const ReplyLabelsSchema = ClassificationObjectSchema.pick({
  stance: true,
  asks: true,
  route: true,
  context: true,
});
export type ReplyLabels = z.infer<typeof ReplyLabelsSchema>;
export const ReplyClassificationSchema = ClassificationObjectSchema.superRefine((value, ctx) => {
  if ((value.stance === "other") !== (value.other_stance !== null))
    ctx.addIssue({
      code: "custom",
      path: ["other_stance"],
      message: "Name other stance exactly when selected",
    });
  if (value.asks.includes("other") !== (value.other_ask !== null))
    ctx.addIssue({
      code: "custom",
      path: ["other_ask"],
      message: "Name other ask exactly when selected",
    });
  const bound = value.ask_bindings.map((b) => b.ask);
  if (
    !unique(bound) ||
    bound.length !== value.asks.length ||
    value.asks.some((a) => !bound.includes(a))
  )
    ctx.addIssue({
      code: "custom",
      path: ["ask_bindings"],
      message: "Bind every ask exactly once to its fact-plan speech acts",
    });
});
export type ReplyClassification = z.infer<typeof ReplyClassificationSchema>;
export const ClassificationCorrectionSchema = z.strictObject({
  kind: z.literal("explicit_opt_out_required"),
  evidence_id: text,
  from_stance: ReplyStanceSchema,
  to_stance: ReplyStanceSchema,
  removed_asks: z.array(ReplyAskSchema),
  reason: z.literal("trigger_authored_text_has_no_explicit_opt_out_request"),
});
export type ClassificationCorrection = z.infer<typeof ClassificationCorrectionSchema>;

const explicitOptOutPatterns = [
  /\bunsubscribe\b/u,
  /\bopt\s+(?:me\s+)?out\b/u,
  /\bremove\s+me\b/u,
  /\bremove\s+.{1,80}\s+from\s+(?:your\s+)?(?:mailing|email|contact)\s+list\b/u,
  /\btake\s+me\s+(?:off|from)\b/u,
  /\bstop\s+(?:emailing|contacting|messaging|mailing)(?:\s+me)?\b/u,
  /\b(?:do\s+not|dont|never)\s+(?:email|contact|message|mail)(?:\s+me)?\b/u,
  /\bno\s+more\s+(?:emails?|messages?|contact)\b/u,
  /\bprosze\s+mnie\s+wypisac\b/u,
  /\bwypisz\s+mnie\b/u,
  /\bnie\s+kontaktuj(?:cie)?\s+sie\s+ze\s+mna\b/u,
] as const;

/**
 * A deterministic, deliberately narrow check for a request to stop future contact.
 *
 * This is not a general intent classifier. Its only authority is to permit suppression after the
 * model selected an opt-out label. A miss degrades to a refusal, which still stops pursuit but does
 * not create a provider suppression. The input must be the trigger's extracted authored text, never
 * the whole thread, because an earlier seller message can itself contain words such as "unsubscribe".
 */
function hasExplicitOptOutRequest(authoredText: string | null): boolean {
  if (authoredText === null) return false;
  const normalized = authoredText
    .normalize("NFKD")
    .replace(/\p{M}/gu, "")
    .toLocaleLowerCase("en-US")
    .replace(/[’']/gu, "")
    .replace(/\s+/gu, " ")
    .trim();
  return explicitOptOutPatterns.some((pattern) => pattern.test(normalized));
}

/** Host enforcement at the model boundary: suppression needs authored opt-out wording. */
export function enforceExplicitOptOut(
  value: ReplyClassification,
  authoredText: string | null,
  evidenceId: string,
): { classification: ReplyClassification; correction: ClassificationCorrection | null } {
  const claimsOptOut = value.stance === "opt_out_request" || value.asks.includes("remove_me");
  if (!claimsOptOut || hasExplicitOptOutRequest(authoredText))
    return { classification: value, correction: null };

  const removedAsks = value.asks.filter((ask) => ask === "remove_me");
  const classification = ReplyClassificationSchema.parse({
    ...value,
    stance: value.stance === "opt_out_request" ? "not_interested_hard" : value.stance,
    asks: value.asks.filter((ask) => ask !== "remove_me"),
    ask_bindings: value.ask_bindings.filter((binding) => binding.ask !== "remove_me"),
  });
  return {
    classification,
    correction: ClassificationCorrectionSchema.parse({
      kind: "explicit_opt_out_required",
      evidence_id: evidenceId,
      from_stance: value.stance,
      to_stance: classification.stance,
      removed_asks: removedAsks,
      reason: "trigger_authored_text_has_no_explicit_opt_out_request",
    }),
  };
}
/**
 * Asks that take a meeting or registration step: the lead wants a time, a meeting's details, a
 * reschedule or a registration. An `interested_engaged` reply with none of these is a conditional
 * yes. The inbox reads agreement from this same list, so it cannot call agreed what drafting holds.
 */
export const SCHEDULING_STEP_ASKS: readonly z.infer<typeof ReplyAskSchema>[] = [
  "propose_times",
  "confirm_their_time",
  "register_attendee",
  "add_attendees",
  "confirm_meeting_details",
  "reschedule",
];
export const ClassificationDecisionSchema = z.strictObject({
  disposition: z.enum(["answer", "auto_reply", "opt_out", "final_refusal", "review_trigger"]),
  turn_treatment: z.enum(["normal", "close_decline", "answer_then_close"]),
  conditional_yes: z.boolean(),
  blocked_next_steps: z.array(campaignActionSchema),
});
export type ClassificationDecision = z.infer<typeof ClassificationDecisionSchema>;

/** A polite hedge with no opening is still a no, not permission to ask a sales question. */
export function enforceBareHedgedNo(
  value: ReplyClassification,
  authoredText: string | null,
): { classification: ReplyClassification; corrected: boolean } {
  if (value.stance !== "not_interested_soft" || authoredText === null)
    return { classification: value, corrected: false };
  const firstParagraph =
    authoredText
      .split(/\n\s*\n/u)[0]
      ?.replace(/\s+/gu, " ")
      .trim() ?? "";
  const withoutGreeting = firstParagraph.replace(
    /^(?:thanks|thank you)(?:\s+for\s+(?:reaching out|your (?:email|message)))?[.!]\s*/iu,
    "",
  );
  if (
    !/^(?:(?:i\s+am|i'm|we\s+are|we're)\s+)?(?:not\s+interested|not\s+for\s+us)(?:\s+(?:at\s+this\s+time|right\s+now|for\s+now))?[.!]?$/iu.test(
      withoutGreeting,
    )
  )
    return { classification: value, corrected: false };
  return {
    classification: ReplyClassificationSchema.parse({ ...value, stance: "not_interested_hard" }),
    corrected: true,
  };
}

/** A lead who asks for a substantive answer has reopened even if she also declines the offer. */
export function hasReopeningAsk(
  value: ReplyLabels & {
    ask_bindings?: readonly {
      ask: z.infer<typeof ReplyAskSchema>;
      timing?: "now" | "later" | undefined;
    }[];
  },
): boolean {
  return value.asks.some(
    (ask) =>
      ask !== "remove_me" &&
      ask !== "stop_until_date" &&
      value.ask_bindings?.find((binding) => binding.ask === ask)?.timing !== "later",
  );
}

function isDeclineUnderPromise(value: ReplyLabels, authoredText: string | null): boolean {
  if (value.stance === "not_interested_soft" || value.stance === "not_interested_hard") return true;
  // A dated decline can be labelled timing_defer. A plain scheduling deferral is not a no.
  return (
    value.stance === "timing_defer" &&
    authoredText !== null &&
    /\b(?:not\s+(?:interested|right\s+now|for\s+us)|no\s+thanks)\b/iu.test(authoredText)
  );
}

/** Classification narrows policy. It never grants campaign permission or attests execution. */
/**
 * Whether the stance is one of `stances`, except that a timing deferral beside an accepted phone
 * call is an accepted future call, not a deferral (`phone-call.ts`).
 */
function phoneAwareStance(
  stance: string,
  stances: readonly string[],
  phoneCallAccepted: boolean | undefined,
): boolean {
  if (!stances.includes(stance)) return false;
  return !(stance === "timing_defer" && phoneCallAccepted);
}

export function hasLaterContactRequest(value: ReplyLabels): boolean {
  return value.stance === "timing_defer" && value.asks.includes("stop_until_date");
}

function closesUnderPromise(
  value: Parameters<typeof hasReopeningAsk>[0],
  policy: {
    noFollowUpAfterNo?: boolean;
    authoredText?: string | null;
    answerLaterRequest?: boolean;
  },
): boolean {
  return (
    policy.noFollowUpAfterNo === true &&
    !(policy.answerLaterRequest === true && hasLaterContactRequest(value)) &&
    isDeclineUnderPromise(value, policy.authoredText ?? null) &&
    !hasReopeningAsk(value)
  );
}

export function classificationDecision(
  value: ReplyLabels & {
    ask_bindings?: readonly {
      ask: z.infer<typeof ReplyAskSchema>;
      timing?: "now" | "later" | undefined;
    }[];
  },
  policy: {
    noFollowUpAfterNo?: boolean;
    authoredText?: string | null;
    /**
     * A hostile complaint with no question asked now is a no: shown with its thread, not drafted
     * (Dawid, 16:31 CEST on 2026-09-29, under the "no" rule of 2026-09-28). The drafting pipeline
     * sets it through the outside-goal patch; absent keeps the rule before, which answered it.
     */
    hostileComplaintIsNo?: boolean;
    /**
     * The rule before commit `9ecbf199` of 2026-09-25: only a hard no with nothing asked closed the
     * turn, and a soft no or a "not now" was answered like any other reply. That commit changed the
     * decision with no patch, so a conversation that parked for the rep under the old rule could no
     * longer replay. The Temporal host sets this for a decision its history made before the commit
     * existed (`docs/2026-10-05-waiting-histories-replay.md`); nothing else may.
     */
    softDeclineIsAnswered?: boolean;
    /** Set by the workflow patch; absent preserves historical decline decisions. */
    answerLaterRequest?: boolean;
    /** False retains the call block for histories before answer-and-offer-call-v1. */
    answerAndOfferCall?: boolean;
    phoneCallAccepted?: boolean;
  } = {},
): ClassificationDecision {
  const conditional_yes =
    value.stance === "interested_engaged" &&
    !value.asks.some((a) => SCHEDULING_STEP_ASKS.includes(a)) &&
    value.route === "none" &&
    !value.context.includes("meeting_already_booked");
  const requestedLater = policy.answerLaterRequest === true && hasLaterContactRequest(value);
  const decline =
    !requestedLater &&
    (phoneAwareStance(
      value.stance,
      ["not_interested_soft", "not_interested_hard", "timing_defer"],
      policy.phoneCallAccepted,
    ) ||
      (policy.hostileComplaintIsNo === true && value.stance === "hostile_complaint"));
  const before9ecbf199 = policy.softDeclineIsAnswered === true;
  const turn_treatment =
    decline && !before9ecbf199
      ? hasReopeningAsk(value)
        ? "answer_then_close"
        : "close_decline"
      : "normal";
  const closesTurn = before9ecbf199
    ? value.stance === "not_interested_hard" && !hasReopeningAsk(value)
    : turn_treatment === "close_decline";
  const disposition =
    value.stance === "not_a_lead_reply"
      ? "review_trigger"
      : value.stance === "auto_reply_ooo" || value.stance === "auto_reply_other"
        ? "auto_reply"
        : value.stance === "opt_out_request" || value.asks.includes("remove_me")
          ? "opt_out"
          : closesTurn || closesUnderPromise(value, policy)
            ? "final_refusal"
            : "answer";
  const blocked: CampaignAction[] = [];
  if (disposition !== "answer") blocked.push(...campaignActionSchema.options);
  else {
    if (before9ecbf199 && value.stance === "not_interested_soft")
      blocked.push(
        ...campaignActionSchema.options.filter((action) => action !== "request_clarification"),
      );
    if (turn_treatment === "answer_then_close")
      blocked.push(
        ...campaignActionSchema.options.filter(
          (action) =>
            !["acknowledge", "answer", "share_resource", "manage_booking"].includes(action),
        ),
      );
    // These are restrictions, not a complete capability/permission registry. In particular,
    // deferral of pursuit does not cancel a simultaneous request for materials or cancellation.
    const paused =
      phoneAwareStance(
        value.stance,
        ["timing_defer", "role_change_departure_auto_notice"],
        policy.phoneCallAccepted,
      ) || value.asks.includes("stop_until_date");
    if (
      (conditional_yes && (policy.answerAndOfferCall === false || !hasReopeningAsk(value))) ||
      (paused && !value.asks.some((a) => a === "propose_times" || a === "confirm_their_time")) ||
      value.stance === "brushoff_soft_yes"
    )
      blocked.push("offer_meeting", "share_booking_link", "handoff");
    else if (conditional_yes) blocked.push("handoff");
    if (paused || value.stance === "brushoff_soft_yes")
      blocked.push("qualify", "collect_contribution", "share_registration_link");
  }
  return ClassificationDecisionSchema.parse({
    disposition,
    turn_treatment: disposition === "opt_out" ? "normal" : turn_treatment,
    conditional_yes,
    blocked_next_steps: blocked,
  });
}

export type SpeechActOwnerCorrection = {
  speech_act_id: string;
  ask: z.infer<typeof ReplyAskSchema>;
  from: z.infer<typeof AskAuthoritySchema>;
  to: "client";
};
/**
 * Tightens the owner of acts bound to a client-authority ask to `client`, and reports each change.
 *
 * For those acts the table already decides the owner and `client` is the only value admission
 * accepts, so the model's label carries no information and a disagreement used to end the run.
 * Alignment only ever moves a label to `client`, the contract's value on doubt: the cost is a round
 * trip to the seller, never an agency answer to a client-owned need. It applies only when every ask
 * bound to the act is client-owned. Unbound acts, acts bound to an agency- or either-authority ask,
 * and acts carrying a mixture keep the model's label, because `stopsTheDraft` reads it and there
 * `client` is the value that blocks least.
 */
export function alignSpeechActOwners<
  Act extends { id: string; owner: z.infer<typeof AskAuthoritySchema> },
>(
  value: Pick<ReplyClassification, "ask_bindings">,
  acts: readonly Act[],
): { acts: Act[]; corrections: SpeechActOwnerCorrection[] } {
  const corrections: SpeechActOwnerCorrection[] = [];
  const aligned = acts.map((act) => {
    const bound = value.ask_bindings.filter((b) => b.speech_act_ids.includes(act.id));
    const binding = bound.find((b) => ASK_AUTHORITY[b.ask] === "client");
    // Every ask on the act must be client-owned. One act carrying both a client-authority and an
    // agency-authority ask has no single correct owner: `client` there would make the agency-owned
    // missing fact advisory in `stopsTheDraft`, so leave it for admission to refuse as before.
    if (!binding || act.owner === "client" || bound.some((b) => ASK_AUTHORITY[b.ask] !== "client"))
      return act;
    corrections.push({ speech_act_id: act.id, ask: binding.ask, from: act.owner, to: "client" });
    return { ...act, owner: "client" as const };
  });
  return { acts: aligned, corrections };
}

export function classificationPlanIssues(
  value: ReplyLabels & Pick<ReplyClassification, "ask_bindings">,
  acts: readonly { id: string; owner: z.infer<typeof AskAuthoritySchema> }[],
  action: CampaignAction | null,
  /** The host decision's blocked steps where it differs from the labels' own (a lifted conditional no). */
  blocked?: readonly CampaignAction[],
): string[] {
  const issues: string[] = [];
  const seen = new Set<string>();
  for (const act of acts) {
    if (seen.has(act.id)) issues.push(`duplicate_speech_act:${act.id}`);
    seen.add(act.id);
  }
  for (const binding of value.ask_bindings)
    for (const id of binding.speech_act_ids) {
      const act = acts.find((a) => a.id === id);
      if (!act) {
        issues.push(`missing_speech_act:${id}`);
        continue;
      }
      const owner = ASK_AUTHORITY[binding.ask];
      if (owner === "client" && act.owner !== "client")
        issues.push(`invalid_ask_owner:${binding.ask}:${id}`);
    }
  if (
    action !== null &&
    (blocked ?? classificationDecision(value).blocked_next_steps).includes(action)
  )
    issues.push(`unaccepted_next_step:${action}`);
  return issues;
}
